import React, { useState, useMemo, useEffect } from "react";
import {
  Button,
  Text,
  Flex,
  useDisclosure,
  Box,
  Avatar,
  Badge,
  HStack,
  VStack,
  Icon,
  Tooltip,
  Progress,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  Right
} from "../../../../components/Card";
import Link from "../../../../components/Link";
import { AddProject as CreateProjectModal } from "../ProjectCreate/ProjectCreate";
import urlToId from "../../../../helpers/UrlToId";
import { translateProjectKind } from "../../../../shared/libs/translate";
import { AddIcon } from "@chakra-ui/icons";
import { FiFolderPlus, FiCalendar, FiUser, FiTrendingUp, FiClock, FiSearch, FiMapPin, FiActivity } from "react-icons/fi";
import { useNavigate } from "react-router";
import client from "../../../../helpers/Client";
import Pagination from "../../../../components/pagination/Pagination";

export default function ProjectListTableView({ Project }) {
  const navigate = useNavigate();
  const createProjectModalDisclosure = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const initialItems = Array.isArray(Project) ? Project : (Project?.results || []);
  const [items, setItems] = useState(initialItems);
  const [currentPage, setCurrentPage] = useState(Array.isArray(Project) ? 1 : (Project?.current_page || 1));
  const [numOfPages, setNumOfPages] = useState(Array.isArray(Project) ? 1 : (Project?.num_of_pages || 1));
  const [pageSize, setPageSize] = useState(Array.isArray(Project) ? Project.length : (Project?.page_size || 20));
  const [numOfItems, setNumOfItems] = useState(Array.isArray(Project) ? Project.length : (Project?.num_of_items || initialItems.length));

  useEffect(() => {
    const nextItems = Array.isArray(Project) ? Project : (Project?.results || []);
    setItems(nextItems);
    setCurrentPage(Array.isArray(Project) ? 1 : (Project?.current_page || 1));
    setNumOfPages(Array.isArray(Project) ? 1 : (Project?.num_of_pages || 1));
    setPageSize(Array.isArray(Project) ? Project.length : (Project?.page_size || 20));
    setNumOfItems(Array.isArray(Project) ? Project.length : (Project?.num_of_items || nextItems.length));
  }, [Project]);

  const fetchPage = async (page) => {
    if (page < 1 || page > numOfPages) return;
    setIsLoading(true);
    try {
      const resp = await client.get("/projects/", { params: { page, page_size: pageSize } });
      const data = resp.data;
      setItems(data.results || []);
      setCurrentPage(data.current_page || page);
      setNumOfPages(data.num_of_pages || 1);
      setNumOfItems(data.num_of_items || (data.results || []).length);
    } finally {
      setIsLoading(false);
    }
  };
  
  const projectCreate = () => {
    navigate("/proje/olustur");
  };
  
  const columns = useBreakpointValue({ base: 1, md: 2, lg: 3, xl: 4 });
  
  const bgGradient = "linear(135deg, white 0%, gray.50 100%)";
  const headerBg = "linear(135deg, red.600 0%, red.900 100%)";
  const cardBg = "white";
  const tableBg = "gray.50";
  const rowHoverBg = "orange.50";
  const textColor = "gray.700";
  const borderColor = "gray.200";

  const getStatusColor = (openTickets, closedTickets) => {
    const total = openTickets + closedTickets;
    if (total === 0) return 'gray';
    const ratio = closedTickets / total;
    if (ratio >= 0.8) return 'green';
    if (ratio >= 0.5) return 'yellow';
    return 'orange';
  };

  const ProjectCard = ({ project }) => {
    const openTickets = project.num_of_open_tickets || 0;
    const closedTickets = project.num_of_closed_tickets || 0;
    const totalTickets = openTickets + closedTickets;
    const completionRate = totalTickets > 0 ? (closedTickets / totalTickets) * 100 : 0;
    
    return (
      <Box
        bg={cardBg}
        borderRadius="2xl"
        p={6}
        shadow="lg"
        border="1px"
        borderColor={borderColor}
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{
          transform: "translateY(-8px) scale(1.02)",
          shadow: "2xl",
          borderColor: "orange.300",
          bgGradient: "linear(135deg, white 0%, orange.50 100%)"
        }}
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          bgGradient: `linear(90deg, orange.400 0%, ${getStatusColor(openTickets, closedTickets)}.400 100%)`,
          borderRadius: "2xl 2xl 0 0"
        }}
      >
        <VStack spacing={4} align="stretch">
          {/* Header */}
          <Flex justify="space-between" align="start">
            <HStack spacing={3}>
              <Avatar
                size="md"
                name={project.name}
                bg="orange.500"
                color="white"
                icon={<FiFolderPlus />}
                shadow="md"
              />
              <VStack align="start" spacing={1}>
                <Link to={`/proje/${urlToId(project.url)}`}>
                  <Text
                    fontWeight="bold"
                    fontSize="lg"
                    color={textColor}
                    _hover={{ color: "orange.500" }}
                    transition="color 0.2s"
                    noOfLines={1}
                  >
                    {project.name}
                  </Text>
                </Link>
                <Text fontSize="xs" color="gray.500">
                  ID: {urlToId(project.url)}
                </Text>
              </VStack>
            </HStack>
            <Badge
              colorScheme="blue"
              variant="subtle"
              px={3}
              py={1}
              borderRadius="full"
              fontSize="xs"
            >
              {translateProjectKind(project.kind)}
            </Badge>
          </Flex>
          
          {/* Stats */}
          <SimpleGrid columns={2} spacing={4}>
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">
                <HStack spacing={1}>
                  <Icon as={FiTrendingUp} />
                  <Text>Birim Sayısı</Text>
                </HStack>
              </StatLabel>
              <StatNumber fontSize="xl" color={textColor}>
                {project.nof_units}
              </StatNumber>
            </Stat>
            
            <Stat>
              <StatLabel fontSize="xs" color="gray.500">
                <HStack spacing={1}>
                  <Icon as={FiActivity} />
                  <Text>Tamamlanma</Text>
                </HStack>
              </StatLabel>
              <StatNumber fontSize="xl" color={getStatusColor(openTickets, closedTickets) + '.500'}>
                {completionRate.toFixed(0)}%
              </StatNumber>
            </Stat>
          </SimpleGrid>
          
          {/* Progress Bar */}
          <Box>
            <Flex justify="space-between" mb={2}>
              <Text fontSize="xs" color="gray.500">İş Emirleri</Text>
              <Text fontSize="xs" color="gray.500">
                {closedTickets}/{totalTickets}
              </Text>
            </Flex>
            <Progress
              value={completionRate}
              colorScheme={getStatusColor(openTickets, closedTickets)}
              size="sm"
              borderRadius="full"
              bg={"gray.100"}
            />
          </Box>
          
          <Divider />
          
          {/* Tickets */}
          <HStack justify="space-between">
            <VStack spacing={1}>
              <Badge colorScheme="orange" variant="solid" borderRadius="full">
                {openTickets}
              </Badge>
              <Text fontSize="xs" color="gray.500">Açık</Text>
            </VStack>
            <VStack spacing={1}>
              <Badge colorScheme="green" variant="solid" borderRadius="full">
                {closedTickets}
              </Badge>
              <Text fontSize="xs" color="gray.500">Kapalı</Text>
            </VStack>
          </HStack>
          
          {/* Location */}
          <HStack spacing={2} color="gray.500">
            <Icon as={FiMapPin} boxSize={4} />
            <Text fontSize="sm">
              {project.city_info?.name || "--"} / {project.district_info?.name || "--"}
            </Text>
          </HStack>
        </VStack>
      </Box>
    );
  };
  return (
    <>
      {createProjectModalDisclosure.isOpen && (
        <CreateProjectModal disclosure={createProjectModalDisclosure} />
      )}
      <Box
        bgGradient={bgGradient}
        borderRadius="2xl"
        p={6}
        shadow="2xl"
        border="1px"
        borderColor={borderColor}
      >
        {/* Modern Header */}
         <Box
        bgGradient={headerBg}
        borderRadius="xl"
        p={6}
        mb={6}
        shadow="lg"
      >
          <Flex justify="space-between" align="center">
            <HStack spacing={4}>
              <Icon as={FiFolderPlus} color="white" boxSize={6} />
              <VStack align="start" spacing={1}>
                <Text
                  fontSize="2xl"
                  fontWeight="bold"
                  color="white"
                  letterSpacing="tight"
                >
                  Proje Yönetimi
                </Text>
                <Text fontSize="sm" color="whiteAlpha.800">
                  Aktif projeler ve ilerleme durumları
                </Text>
              </VStack>
            </HStack>
            <Flex gap={3}>
              <Button
                onClick={createProjectModalDisclosure.onOpen}
                rightIcon={<AddIcon />}
                bgGradient="linear(135deg, white 0%, gray.100 100%)"
                color="gray.700"
                _hover={{
                  bgGradient: "linear(135deg, gray.100 0%, gray.200 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.15)"
                }}
                transition="all 0.3s ease"
                borderRadius="xl"
                px={6}
                py={3}
                fontWeight="semibold"
              >
                Proje Ekle
              </Button>
            </Flex>
          </Flex>
        </Box>
        
        {/* Projects Grid */}
        {isLoading ? (
          <SimpleGrid columns={columns} spacing={6}>
            {Array.from({ length: pageSize }).map((_, idx) => (
              <Box key={idx} bg={cardBg} borderRadius="2xl" p={6} shadow="lg" border="1px" borderColor={borderColor}>
                <Box height="160px" bg="gray.100" borderRadius="lg" />
              </Box>
            ))}
          </SimpleGrid>
        ) : items.length > 0 ? (
          <SimpleGrid columns={columns} spacing={6}>
            {items.map((project) => (
              <ProjectCard key={project.url} project={project} />
            ))}
          </SimpleGrid>
        ) : (
          <Box
            bg={cardBg}
            borderRadius="2xl"
            p={12}
            textAlign="center"
            border="1px"
            borderColor={borderColor}
          >
            <VStack spacing={4}>
              <Icon as={FiFolderPlus} boxSize={16} color="gray.400" />
              <Text fontSize="xl" color="gray.500" fontWeight="medium">
                Henüz proje kaydı bulunmuyor
              </Text>
              <Text fontSize="sm" color="gray.400">
                İlk projenizi eklemek için yukarıdaki butonu kullanın
              </Text>
            </VStack>
          </Box>
        )}
        <Box mt={6}>
          <Pagination path={"proje"} num_of_items={numOfItems} num_of_pages={numOfPages} currentPage={currentPage} />
        </Box>
      </Box>
    </>
  );
}
